<script setup lang="ts">
import { computed } from "vue";
// Import weather icon assets from rainowls-ui package
import Cloud from "rainowls-ui/src/assets/icons/weather-detailed/Cloud.svg";
import Cloudy from "rainowls-ui/src/assets/icons/weather-detailed/Cloudy.svg";
import Cloudy80 from "rainowls-ui/src/assets/icons/weather-detailed/Cloudy80.svg";
import CloudyFreezingRain from "rainowls-ui/src/assets/icons/weather-detailed/CloudyFreezingRain.svg";
import CloudyLightMist from "rainowls-ui/src/assets/icons/weather-detailed/CloudyLightMist.svg";
import CloudyMist from "rainowls-ui/src/assets/icons/weather-detailed/CloudyMist.svg";
import CloudyRain from "rainowls-ui/src/assets/icons/weather-detailed/CloudyRain.svg";
import CloudyRainDrops from "rainowls-ui/src/assets/icons/weather-detailed/CloudyRainDrops.svg";
import Fog from "rainowls-ui/src/assets/icons/weather-detailed/Fog.svg";
import FreezingFog from "rainowls-ui/src/assets/icons/weather-detailed/FreezingFog.svg";
import Moon from "rainowls-ui/src/assets/icons/weather-detailed/Moon.svg";
import MoonHaze from "rainowls-ui/src/assets/icons/weather-detailed/MoonHaze.svg";
import RainShowers from "rainowls-ui/src/assets/icons/weather-detailed/RainShowers.svg";
import RainSnow from "rainowls-ui/src/assets/icons/weather-detailed/RainSnow.svg";
import RainSnowShowers from "rainowls-ui/src/assets/icons/weather-detailed/RainSnowShowers.svg";
import Snow from "rainowls-ui/src/assets/icons/weather-detailed/Snow.svg";
import SnowHeavy from "rainowls-ui/src/assets/icons/weather-detailed/SnowHeavy.svg";
import SnowLight from "rainowls-ui/src/assets/icons/weather-detailed/SnowLight.svg";
import SnowShowers from "rainowls-ui/src/assets/icons/weather-detailed/SnowShowers.svg";
import Sunny from "rainowls-ui/src/assets/icons/weather-detailed/Sunny.svg";
import SunnyHaze from "rainowls-ui/src/assets/icons/weather-detailed/SunnyHaze.svg";
import Thunderstorm from "rainowls-ui/src/assets/icons/weather-detailed/Thunderstorm.svg";
import ThunderstormChance from "rainowls-ui/src/assets/icons/weather-detailed/ThunderstormChance.svg";
import Windy from "rainowls-ui/src/assets/icons/weather-detailed/Windy.svg";

interface Props {
  weatherCode: number | null;
  windSpeed: number | null;
  isDay: boolean;
  size?: number; // Size in pixels
}

const props = withDefaults(defineProps<Props>(), {
  weatherCode: 0,
  windSpeed: 0,
  isDay: true,
  size: 100,
});

const icons = {
  Cloud,
  Cloudy,
  Cloudy80,
  CloudyFreezingRain,
  CloudyLightMist,
  CloudyMist,
  CloudyRain,
  CloudyRainDrops,
  Fog,
  FreezingFog,
  Moon,
  MoonHaze,
  RainShowers,
  RainSnow,
  RainSnowShowers,
  Snow,
  SnowHeavy,
  SnowLight,
  SnowShowers,
  Sunny,
  SunnyHaze,
  Thunderstorm,
  ThunderstormChance,
  Windy,
};

// Weather code arrays (matching iOS implementation)
const sunMoonCodes = [
  0, 1, 2, 40, 41, 42, 43, 80, 81, 82, 83, 84, 89, 90, 85, 86, 95, 96,
];

const sunMoonHazeCodes = [4, 5, 6, 7, 8, 9];

const cloudCodes = [
  3, 22, 28, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 56, 57, 68, 69, 70, 71, 72,
  73, 74, 75, 94,
];

const cloudSmallCodes = [
  31, 34, 35, 36, 39, 80, 81, 82, 83, 84, 85, 86, 88, 89, 90,
];

const fogCodes = [28, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49];
const mistCodes = [52, 53, 54, 55];
const lightRainCodes = [58, 59, 60, 61, 91];
const rainCodes = [21, 62, 63, 64, 65, 92];
const freezingRainCodes = [24, 66, 67, 76, 77, 78, 79];
const heavySnowCodes = [74, 75, 94];
const snowShowersCodes = [85, 86];
const rainShowersCodes = [80, 81, 82];
const rainSnowShowersCodes = [83, 84, 87, 88, 89, 90];
const thunderStormChanceCodes = [95, 96];
const thunderStormCodes = [97, 98, 99];

// Computed icon paths
const baseIcon = computed(() => {
  const code = props.weatherCode ?? 0;
  if (sunMoonHazeCodes.includes(code))
    return props.isDay ? icons.SunnyHaze : icons.MoonHaze;
  if (mistCodes.includes(code)) return icons.CloudyMist;
  if (code === 50 || code === 51) return icons.CloudyLightMist;
  if (lightRainCodes.includes(code)) return icons.CloudyRainDrops;
  if (rainCodes.includes(code)) return icons.CloudyRain;
  if (freezingRainCodes.includes(code)) return icons.CloudyFreezingRain;
  if (thunderStormCodes.includes(code)) return icons.Thunderstorm;
  if (sunMoonCodes.includes(code))
    return props.isDay ? icons.Sunny : icons.Moon;
  if (code === 3) return icons.Cloudy;
  return null;
});

const showWindy = computed(() => (props.windSpeed ?? 0) > 16);

const showCloud = computed(() => {
  const code = props.weatherCode ?? 0;
  if (code === 1) return "small";
  if (code === 2) return "medium";
  if (code === 3) return "full";
  if (cloudCodes.includes(code)) return "full";
  if (cloudSmallCodes.includes(code)) return "tiny";
  return null;
});

const showFog = computed(() => {
  const code = props.weatherCode ?? 0;
  if (code === 56 || code === 57) return "freezing";
  if (fogCodes.includes(code)) return "normal";
  return null;
});

const showSnow = computed(() => {
  const code = props.weatherCode ?? 0;
  if (code === 23 || code === 68 || code === 69) return "rainSnow";
  if (code === 70 || code === 71) return "light";
  if (code === 22 || code === 72 || code === 73) return "normal";
  if (heavySnowCodes.includes(code)) return "heavy";
  return null;
});

const showShowers = computed(() => {
  const code = props.weatherCode ?? 0;
  if (rainShowersCodes.includes(code)) return "rain";
  if (rainSnowShowersCodes.includes(code)) return "rainSnow";
  if (snowShowersCodes.includes(code)) return "snow";
  if (thunderStormChanceCodes.includes(code)) return "thunderstorm";
  return null;
});
</script>

<template>
  <div class="relative" :style="{ width: `${size}px`, height: `${size}px` }">
    <!-- Base Clear/Haze/Mist/Rain/Thunderstorm conditions (z-index 0) -->
    <img
      v-if="baseIcon"
      :src="baseIcon"
      class="absolute h-full w-full object-contain"
      alt="Weather condition"
    />

    <!-- Cloud layers (z-index 10) -->
    <img
      v-if="showCloud === 'small'"
      :src="icons.Cloud"
      class="absolute object-contain opacity-75"
      :style="{
        width: '60%',
        height: '60%',
        top: '15%',
        left: '15%',
        zIndex: 10,
      }"
    />
    <img
      v-else-if="showCloud === 'medium'"
      :src="icons.Cloudy80"
      class="absolute object-contain opacity-90"
      :style="{
        width: '90%',
        height: '90%',
        top: '10%',
        left: '10%',
        zIndex: 10,
      }"
    />
    <img
      v-else-if="showCloud === 'full'"
      :src="weatherCode === 3 ? icons.Cloudy : icons.Cloud"
      class="absolute z-10 h-full w-full object-contain"
    />
    <img
      v-else-if="showCloud === 'tiny'"
      :src="icons.Cloud"
      class="absolute object-contain"
      :style="{
        width: '75%',
        height: '75%',
        top: '10%',
        left: '15%',
        zIndex: 10,
      }"
    />

    <!-- Fog layers (z-index 20) -->
    <img
      v-if="showFog === 'normal'"
      :src="icons.Fog"
      class="absolute object-contain drop-shadow-md"
      :style="{
        width: '85%',
        height: '85%',
        top: '15%',
        zIndex: 20,
      }"
    />
    <img
      v-else-if="showFog === 'freezing'"
      :src="icons.FreezingFog"
      class="absolute object-contain drop-shadow-md"
      :style="{
        width: '85%',
        height: '85%',
        top: '15%',
        zIndex: 20,
      }"
    />

    <!-- Snow layers (z-index 30) -->
    <img
      v-if="showSnow === 'rainSnow'"
      :src="icons.RainSnow"
      class="absolute object-contain drop-shadow-md"
      :style="{
        width: '85%',
        height: '85%',
        top: '25%',
        zIndex: 30,
      }"
    />
    <img
      v-else-if="showSnow === 'light'"
      :src="icons.SnowLight"
      class="absolute object-contain drop-shadow-md"
      :style="{
        width: '85%',
        height: '85%',
        top: '25%',
        zIndex: 30,
      }"
    />
    <img
      v-else-if="showSnow === 'normal'"
      :src="icons.Snow"
      class="absolute object-contain drop-shadow-md"
      :style="{
        width: '85%',
        height: '85%',
        top: '25%',
        zIndex: 30,
      }"
    />
    <img
      v-else-if="showSnow === 'heavy'"
      :src="icons.SnowHeavy"
      class="absolute object-contain drop-shadow-md"
      :style="{
        width: '85%',
        height: '85%',
        top: '25%',
        zIndex: 30,
      }"
    />

    <!-- Shower layers (z-index 40) -->
    <img
      v-if="showShowers === 'rain'"
      :src="icons.RainShowers"
      class="absolute object-contain drop-shadow-md"
      :style="{
        width: '45%',
        height: '45%',
        top: '30%',
        left: '15%',
        zIndex: 40,
      }"
    />
    <img
      v-else-if="showShowers === 'rainSnow'"
      :src="icons.RainSnowShowers"
      class="absolute object-contain drop-shadow-md"
      :style="{
        width: '55%',
        height: '55%',
        top: '30%',
        left: '15%',
        zIndex: 40,
      }"
    />
    <img
      v-else-if="showShowers === 'snow'"
      :src="icons.SnowShowers"
      class="absolute object-contain drop-shadow-md"
      :style="{
        width: '55%',
        height: '55%',
        top: '30%',
        left: '15%',
        zIndex: 40,
      }"
    />
    <img
      v-else-if="showShowers === 'thunderstorm'"
      :src="icons.ThunderstormChance"
      class="absolute object-contain drop-shadow-md"
      :style="{
        width: '65%',
        height: '65%',
        top: '20%',
        left: '15%',
        zIndex: 40,
      }"
    />

    <!-- Windy overlay (z-index 50) -->
    <img
      v-if="showWindy"
      :src="icons.Windy"
      class="absolute object-contain drop-shadow-md"
      :style="{
        width: '65%',
        height: '65%',
        top: '20%',
        left: '10%',
        zIndex: 50,
      }"
    />
  </div>
</template>
